import {CheckInOutPair} from "@src/httpClient/__generated__/Api.ts";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {
    Body_14_Coral_Empasis_14,
    Body_14_Coral_Primary_Regular_14, Body_14_Coral_Strong_emphasis_14,
    Body_16_Coral_Emphasis_16,
} from "@UI/typography/generated/typography.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {getTimeFromDT} from "@src/utils/formatDate.ts";
import {useMemo} from "react";

import styles from "./historyPopupElement.module.less";
import cn from "classnames";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {MapPinWhite} from "@UI/components/icons/MapPinWhite.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {MapPinOff} from "@UI/components/icons/MapPinOff.tsx";
import {Clock} from "@UI/components/icons/Clock.tsx";
import {LocalStatus} from "@src/db.ts";
import {getLocationLink, getLocationsString} from "@src/utils/coordLink.ts";

interface Props {
    events?: CheckInOutPair
}

interface LocationLinkProps {
    link?: string
    coords: string
}

const LocationLink = ({link, coords}: LocationLinkProps) => {
    if (link) return (
        <Button type={ButtonType.link} icon={<MapPinWhite size={20}/>}
                className={styles.coordsButton}
                color={ColorNames.CORAL_INK}
                externalLink={true}
                href={link}>
            <Body_14_Coral_Empasis_14>
                {coords}
            </Body_14_Coral_Empasis_14>
        </Button>
    )
    return (
        <Stack alignItems={"center"} className={styles.errorTime} gap={10}>
            <MapPinOff color={Colors.Coral_special_Error_red}/>
            <Body_14_Coral_Strong_emphasis_14>
                <Gt>No location information</Gt>
            </Body_14_Coral_Strong_emphasis_14>
        </Stack>
    )
}

export const HistoryPopupElement = ({events}: Props) => {
    const {inCoords, inLink, inStatus} = useMemo(() => {
        if (!events?.event_in) return {
            inCoords: "—,—",
            inLink: undefined,
            inStatus: undefined
        };
        return {
            inCoords: getLocationsString(events?.event_in),
            inLink: events?.event_in?.latitude && events?.event_in?.longitude ? getLocationLink(events?.event_in) : undefined,
            inStatus: "localStatus" in events.event_in  ? events.event_in.localStatus : undefined
        }
    }, [events])

    const {outCoords, outLink, outStatus} = useMemo(() => {
        if (!events?.event_out) return {
            outCoords: "—,—",
            outLink: undefined,
            outStatus: undefined,
        };
        return {
            outCoords: getLocationsString(events?.event_out),
            outLink: events?.event_out?.latitude && events?.event_out?.longitude ? getLocationLink(events?.event_out) : undefined,
            outStatus: events.event_out && "localStatus" in events.event_out ? events.event_out.localStatus : undefined
        }
    }, [events])

    return (
        <Stack direction={"column"} gap={16} className={styles.extendedHistoryElem}>
            <Stack alignItems={"center"} className={cn(styles.historySub)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Start work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack className={cn({[styles.offlineEvent]: inStatus === LocalStatus.pending})}>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {events?.event_in?.recorded_at
                                    ? getTimeFromDT(events?.event_in?.recorded_at)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {events?.event_in?.recorded_at
                                ? <LocationLink link={inLink} coords={inCoords} />
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>
                            }
                        </Stack>
                    </Stack>

                    {(inStatus === LocalStatus.pending) &&
                        <Body_14_Coral_Primary_Regular_14 style={{color: Colors.Coral_mango_500}}>
                            <Gt>Unconfirmed stamp</Gt>
                        </Body_14_Coral_Primary_Regular_14>
                    }

                </Stack>
            </Stack>
            <Stack alignItems={"center"} className={cn(styles.historySub, styles.topBorder)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Finish work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack className={cn({[styles.offlineEvent]: outStatus === LocalStatus.pending})}>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {events?.event_out?.recorded_at
                                    ? getTimeFromDT(events?.event_out?.recorded_at)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {events?.event_out?.recorded_at
                                ?
                                <LocationLink link={outLink} coords={outCoords} />
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>
                            }
                        </Stack>
                    </Stack>

                    {(outStatus === LocalStatus.pending) &&
                        <Body_14_Coral_Primary_Regular_14 style={{color: Colors.Coral_mango_500}}>
                            <Gt>Unconfirmed stamp</Gt>
                        </Body_14_Coral_Primary_Regular_14>
                    }
                </Stack>
            </Stack>
        </Stack>
    )
}